<template>
  <div class="account-detail-container">
    <router-link to="/dashboard" class="back-link">&larr; Kembali ke Dasbor</router-link>

    <h1>Riwayat Transaksi</h1>
    <p v-if="account">Untuk Rekening: <strong>{{ account.account_number }}</strong></p>

    <button
      @click="downloadPdf"
      :disabled="isDownloading"
      class="btn btn-primary"
      style="margin: 1rem 0;"
    >
      {{ isDownloading ? 'Mengunduh...' : 'Unduh Laporan (PDF)' }}
    </button>

    <div v-if="loading" class="loading">Memuat riwayat...</div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div v-if="!loading && processedTransactions.length === 0 && !error" class="no-transactions">
      Belum ada transaksi untuk rekening ini.
    </div>

    <!-- Tabel Transaksi dengan Saldo Berjalan -->
    <table
      v-if="!loading && processedTransactions.length > 0"
      class="transactions-table"
    >
      <thead>
        <tr>
          <th>Tanggal</th>
          <th>Sandi</th>
          <th>Deskripsi</th>
          <th>Debet</th>
          <th>Kredit</th>
          <th>Saldo</th>
        </tr>
      </thead>
      <tbody>
        <!-- Saldo Awal -->
        <tr class.initial-balance>
          <td colspan="5"><strong>Saldo Awal Periode</strong></td>
          <td class="balance"><strong>{{ formatCurrency(initialBalance) }}</strong></td>
        </tr>
        <!-- Transaksi -->
        <tr v-for="tx in processedTransactions" :key="tx.id">
          <td>{{ formatDateTime(tx.timestamp) }}</td>
          <td>{{ tx.transaction_code?.code || '-' }}</td>
          <!-- Gunakan fungsi baru untuk deskripsi dinamis -->
          <td>{{ generateTransactionDescription(tx) }}</td>
          <td class="amount-debit">{{ tx.debit ? formatCurrency(tx.amount) : '' }}</td>
          <td class="amount-credit">{{ tx.credit ? formatCurrency(tx.amount) : '' }}</td>
          <td class="balance">{{ formatCurrency(tx.running_balance) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/services/api';

// Props dari router
const props = defineProps({
  id: {
    type: [String, Number],
    required: true
  }
});

// State reaktif
const route = useRoute();
const transactions = ref([]);
const loading = ref(true);
const error = ref(null);
const isDownloading = ref(false);

// Mengambil data rekening dari query parameter yang dikirim oleh Dashboard
const account = computed(() => {
  if (route.query.account) {
    try {
      return JSON.parse(route.query.account);
    } catch {
      return null;
    }
  }
  return null;
});


const generateTransactionDescription = (tx) => {
// const isDebit = tx.source_account_id === props.id;
  const code = tx.transaction_code?.internal_type;

  if (code === 'TRANSFER_OUT' && tx.destination_account?.owner) {
    return `Transfer ke ${tx.destination_account.owner.full_name} (${tx.destination_account.account_number})`;
  }
  if (code === 'TRANSFER_IN' && tx.source_account?.owner) {
    return `Transfer dari ${tx.source_account.owner.full_name} (${tx.source_account.account_number})`;
  }
  // Untuk deskripsi default, gunakan deskripsi dari DB atau nama kode transaksi
  return tx.description || tx.transaction_code?.name || 'Transaksi';
};

// Saldo akhir rekening, digunakan sebagai titik awal kalkulasi
const currentAccountBalance = computed(() => {
  return account.value ? parseFloat(account.value.balance) : 0;
});

// Saldo awal periode (sebelum transaksi pertama ditampilkan)
const initialBalance = computed(() => {
  if (transactions.value.length === 0) {
    return currentAccountBalance.value;
  }
  
  // Hitung mundur dari saldo akhir untuk menemukan saldo awal
  let balance = currentAccountBalance.value;
  for (const tx of transactions.value) {
    if (tx.source_account_id == props.id) balance += parseFloat(tx.amount); // Jika debit, tambahkan kembali
    if (tx.destination_account_id == props.id) balance -= parseFloat(tx.amount); // Jika kredit, kurangi kembali
  }
  return balance;
});

// Proses transaksi untuk ditampilkan di tabel
const processedTransactions = computed(() => {
  console.log("3. Computed property 'processedTransactions' dijalankan."); 
  if (transactions.value.length === 0) return [];

  let runningBalance = initialBalance.value; // Mulai dari saldo awal
  const processed = [];
  
  // Urutkan dari terlama ke terbaru. API mengembalikan terbaru -> terlama.
  const sortedTransactions = [...transactions.value].reverse();

  for (const tx of sortedTransactions) {
    const isDebit = tx.source_account_id == props.id;
    
    // Perbarui saldo berjalan SETELAH setiap transaksi
    if (isDebit) {
      runningBalance -= parseFloat(tx.amount);
    } else {
      runningBalance += parseFloat(tx.amount);
    }
    
    processed.push({
      ...tx,
      debit: isDebit,
      credit: !isDebit,
      running_balance: runningBalance,
    });
  }
  console.log("4. Hasil olahan transaksi:", processed);
  return processed;
});

// Fungsi untuk mengunduh PDF
const downloadPdf = async () => {
  isDownloading.value = true;
  try {
    const response = await api.downloadStatement(props.id);
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;

    let fileName = `laporan-rekening-${props.id}.pdf`;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match && match[1]) fileName = match[1];
    }

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Gagal mengunduh PDF:', err);
    alert('Gagal mengunduh laporan.');
  } finally {
    isDownloading.value = false;
  }
};

// Fungsi bantuan untuk formatting
const formatCurrency = (value) => {
  const num = parseFloat(value);
  return isNaN(num)
    ? '0.00'
    : num.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const formatDateTime = (value) => {
  return new Date(value).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
};

// Hook saat komponen dimuat
onMounted(async () => {
  loading.value = true;
  error.value = null;
try {
    const response = await api.getTransactionsForAccount(props.id);
    console.log("1. Data mentah dari API:", response.data); // <-- LOG 1
    transactions.value = response.data;
    console.log("2. State 'transactions' diisi:", transactions.value); // <-- LOG 2
  } catch (err) {
    console.error('Gagal mengambil riwayat transaksi:', err);
    error.value = 'Tidak dapat memuat riwayat transaksi.';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.account-detail-container {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.back-link {
  display: inline-block;
  margin-bottom: 16px;
  text-decoration: none;
  color: #007bff;
  font-weight: 500;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}
.btn-primary:hover:not(:disabled) {
  background-color: #0069d9;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading,
.error-message,
.no-transactions {
  padding: 16px;
  margin: 16px 0;
  border-radius: 4px;
}
.loading {
  background-color: #e9ecef;
}
.error-message {
  background-color: #f8d7da;
  color: #721c24;
}
.no-transactions {
  background-color: #f8f9fa;
  text-align: center;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.transactions-table th,
.transactions-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #dee2e6;
}

.transactions-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.initial-balance td {
  font-weight: bold;
  background-color: #f1f3f5;
}

.amount-debit {
  color: #e74c3c;
  text-align: right;
}
.amount-credit {
  color: #27ae60;
  text-align: right;
}
.balance {
  font-weight: 600;
  text-align: right;
  color: #2c3e50;
}
</style>